/**
 * Spoken production (TASKS T61, SPEC §5, RESEARCH §5).
 *
 * The learner says the Spanish aloud; the recognizer returns a transcript; the transcript is graded
 * exactly like a typed answer, and the feedback then names **which sounds** differed.
 *
 * ## What the evidence permits this mode to claim
 *
 * ASR pronunciation training shows g ≈ 0.69 overall, **large on segmental features and small on
 * suprasegmental**, with explicit corrective feedback beating plain dictation (Ngo, Chen & Lai 2024,
 * MODERATE confidence). Three consequences, and they are constraints rather than features:
 *
 *  1. **Per-sound feedback, never a pass/fail verdict on the pronunciation.** "rr is a rolled r,
 *     held" is the mode's whole output. There is no score, no percentage, and no stars.
 *  2. **Nothing is said about intonation, rhythm, stress or fluency.** The app cannot hear those and
 *     must never imply it can — a claim it cannot support would make every other claim suspect.
 *  3. **No speech metric is surfaced as meaningful before week five** (T64). Under four weeks of ASR
 *     training is rarely effective, so an early number would be noise presented as progress.
 *
 * ## The transcript is the answer
 *
 * A spoken attempt is graded by the same validator as a typed one, against the same expected string.
 * This matters: speech is a **production** mode, so it advances FSRS intervals like any other
 * (invariant 1), and it would be dishonest for it to be graded more leniently just because it is
 * harder to get a transcript out of a phone. The segmental notes are *additional* — they explain a
 * result, they do not soften it.
 *
 * Pure. No DOM, no clock, no store, and no recognizer: this module never touches a microphone. It
 * describes a card and reads a transcript.
 */

import { diffPhonemes, phonemesOfPhrase, segmentalNotes, type SegmentalNote } from '../lang/phonemes';
import type { SpeechFailure } from '../speech/recognizer';
import type { ModeInput, Presentation } from './types';

/**
 * What the learner is told when the microphone produced nothing usable.
 *
 * One line per outcome, each naming something he can actually do. A failed utterance is **not** a
 * wrong answer and must never be banked as one: nothing was produced, so there is nothing to grade,
 * and marking it wrong would let a noisy room lower a retention figure.
 */
export const SPEECH_TROUBLE: Readonly<Record<SpeechFailure, string>> = {
  'no-speech': 'Nothing was heard. Try again, a little closer.',
  'not-allowed': 'The microphone is blocked. Allow it in Safari, or type the answer instead.',
  engine: 'Speech is not working just now. Type the answer instead.',
  aborted: 'Stopped listening.',
};

/**
 * The help this card offers if asked (invariant 2 — asking marks the attempt hinted, T54).
 *
 * The first sound rather than the first letter, because that is the thing being practised: `gente`
 * starts with the j-sound, and telling him it "starts with g" would be answering a different
 * question in a way that actively misleads.
 */
export function speechScaffold(expected: string): string | null {
  const sounds = phonemesOfPhrase(expected);
  const first = sounds[0];
  if (!first) return null;
  return `It starts with the sound of "${first.letters}".`;
}

/**
 * What differed, sound by sound.
 *
 * Returns nothing at all when the transcript matched — there is no "well done, but" here, and a
 * correct answer that came with a list of notes would read as one that was not quite correct.
 */
export function segmentalFeedback(expected: string, heard: string): SegmentalNote[] {
  if (heard.trim().length === 0) return [];
  return segmentalNotes(diffPhonemes(phonemesOfPhrase(expected), phonemesOfPhrase(heard)));
}

/**
 * Tier 1 is a different question, and shows the Spanish.
 *
 * Sonido is **phonology**, not vocabulary: the task is to produce the sound correctly, and hiding the
 * word would turn it into a recall test that happens to be spoken — which is tier 2's job. So the
 * word is on screen, the gloss says which sound it is about, and what is graded is what came back
 * from the microphone.
 *
 * Every other tier hides it: there, the Spanish *is* the answer.
 */
const SOUND_TIER = 1;

/**
 * Build the spoken-production card.
 *
 * Above tier 1 the English is the prompt and the Spanish is what he says, which makes this the
 * hardest card in the game after the free-typed sentence: nothing on screen is the answer.
 */
export function speechPresentation(input: ModeInput): Presentation {
  const { item } = input;

  if (item.tier === SOUND_TIER) {
    const spoken = { kind: 'text' as const, text: item.es };
    return {
      mode: 'speech',
      instruction: 'Say this out loud',
      segments: [spoken],
      // The gloss carries the sound being practised — "rolled rr, not the tap in pero" — because on
      // this tier that *is* the instruction, and it is what the feedback will talk about.
      gloss: item.en,
      expected: item.es,
      // Nothing to hint: the word is already on screen, and the sound is what the gloss says.
      scaffold: null,
      input: 'speech',
      fieldLabel: 'What was heard',
      announce: `${item.es}. ${item.en}.`,
    };
  }

  return {
    mode: 'speech',
    instruction: 'Say it in Spanish',
    segments: [{ kind: 'text', text: item.en }],
    gloss: null,
    expected: item.es,
    scaffold: speechScaffold(item.es),
    input: 'speech',
    fieldLabel: 'What was heard',
    announce: `${item.en}. Say it in Spanish.`,
  };
}
